/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides header for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import React, { FunctionComponent, MouseEvent, useCallback, useContext, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';


import { IAuthSession } from '@servicestack/client';

import {
    AppBar,
    Box,
    Button,
    collapseClasses,
    darken,
    Divider,
    IconButton,
    lighten,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    styled,
    SwipeableDrawer,
    Theme,
    Toolbar,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';

import { faMoon, faSun, faUserCircle } from '@fortawesome/pro-duotone-svg-icons';

import { faBars } from '@fortawesome/pro-solid-svg-icons';

import {Link, NavLink, useLocation } from 'react-router-dom';
import useIsMobile from '@/hooks/useIsMobile';
import ChosenThemeContext from '@/contexts/styles/ChosenThemeContext';
import ChosenThemeActionContext from '@/contexts/ChosenThemeActionContext';
import ToggleFeedbackButton from '@/components/ToggleFeedbackButton';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

export interface IMenuItem {
    icon: IconDefinition;
    label: React.ReactNode;
    url: string;
    external: boolean;
    newTab: boolean;
}

interface IHeaderProps {
    /**
     * Menu Items to display in the header 
     */
    menuItems?: IMenuItem[];

    user?: IAuthSession | null;

    trialName: string;
    protocolId: string | undefined;
    organisationName: string;
    organisationLogoLight: string | null | undefined;
    organisationLogoDark: string | null | undefined;
    organisationLogoMobileLight: string | null | undefined;
    organisationLogoMobileDark: string | null | undefined;
    applyDarkMode?: (apply: boolean) => void;
    overrideTheme?: boolean;
}



/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */
const StyledAppBar = styled(AppBar)(({ theme }) => ({
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.mode === 'dark' ?
        '#121212' :
        'white',
    backgroundImage: theme.palette.mode === 'dark' ?
        'linear-gradient(rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.09))' :
        undefined
}))

const StyledToolbar = styled(Toolbar)(({ theme}) => ({
    background: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.primary.main,

    
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 3),
    // minHeight: '64px !important',

    '& > div': {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },

    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(0, 1.5),
        gap: theme.spacing(5),
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 1.5),
        gap: theme.spacing(3),
    }
}))

const LogoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: `${theme.spacing(1)} !important`,
}))


const LogoBox = styled(Box)(({ theme}) => ({
    display: 'flex',
    height: '64px',
    margin: theme.spacing(0),

    '& >img': {
        height: '64px',
    },

    [theme.breakpoints.only('xs')]: {
        display: 'none'
    },
}));

const PublicLogoBox = styled(Box)(({ theme}) => ({
    display: 'flex',
    height: '64px',
    margin: theme.spacing(0),

    '& >img': {
        height: '64px',
    },
}));


const LogoMobileImage = styled('img')(({ theme}) => ({
    width: '250px',
    height: 'auto',
    padding: theme.spacing(3),
}));


const Title = styled(Typography)(({ theme}) => ({
    color: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.primary.main,
    fontWeight: 'bold',

    [theme.breakpoints.only('xs')]: {
        display: 'none'
    }
}));


const TrialUserContainer = styled(Box)(({ theme}) => ({
    gap: theme.spacing(0.5)
}));


const TrialName = styled(Typography)(({ theme}) => ({
    color: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.primary.main,

    fontSize: '1rem',
    fontWeight: 600,
    textDecoration: 'none',
    lineHeight: '1.25rem',

    
    //marginTop: theme.spacing(0.4),

    [theme.breakpoints.only('xs')]: {
        color: theme.palette.mode === 'dark' ?
            undefined :
            theme.palette.primary.main,
    },
}));


const TrialProtocolId = styled(Typography)(({ theme }) => ({
    color: theme.palette.mode === 'dark' ?
        darken(theme.palette.common.white, 0.5) :
        lighten(theme.palette.common.black, 0.5),

    fontSize: '0.85rem',
    fontWeight: 400,
    textDecoration: 'none',
}));


const TrialNameArea = styled(Box)(({ theme }) => ({
    paddingLeft: theme.spacing(1.5),
    display: 'flex', 
    flexDirection: 'column', 
    justifyContent: 'center', 
    alignItems: 'flex-start' 
}));


const StyledLink = styled(Link)(() => ({
    color: 'inherit',
    textDecoration: 'none',
    display: 'flex',

    [`& .${collapseClasses.root}`]: {
        width: '0px !important',
        transitionProperty: 'width !important'
    },
    [`& .${collapseClasses.entered}`]: {
        width: '100% !important'
    },
    [`& .${collapseClasses.hidden}`]: {
        width: '0px !important'
    }
}));


const StyledAnchor = styled('a')(() => ({
    color: 'inherit',
    textDecoration: 'none',
    display: 'flex',

    [`& .${collapseClasses.root}`]: {
        width: '0px !important',
        transitionProperty: 'width !important'
    },
    [`& .${collapseClasses.entered}`]: {
        width: '100% !important'
    },
    [`& .${collapseClasses.hidden}`]: {
        width: '0px !important'
    }
}));


const MenuContainer = styled(Box)(() => ({
    flexGrow: 1,
    justifyContent: 'flex-end !important',
    display: 'flex',
    wrap: 'nowrap',
}));


const MenuDivider = styled(Box)(({ theme}) => ({
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    margin : theme.spacing(0,0,0,1),
    height: '1.25rem',
    zIndex: 1000
}));


const NavigationLink = styled(Button, { shouldForwardProp: prop => prop !== 'active' })(({ theme, active}: {theme: Theme, active: boolean}) => ({

    display: 'flex',
    flexWrap: 'nowrap',
    borderRadius: 0,
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '1rem',
    height: '100%',
    backgroundPosition: 'bottom left',
    backgroundRepeat: 'no-repeat',
    backgroundColor: theme.palette.mode === 'dark' ?
        '#121212' :
        'white',
    backgroundImage: active ?
        theme.palette.mode === 'dark' ?
            `linear-gradient(rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.09)), linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.main})` :
            `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.main})`:
        theme.palette.mode === 'dark' ?
            'linear-gradient(rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.09))' :
            undefined,
    backgroundSize: active ? 
        theme.palette.mode === 'dark' ? 
            `cover,100% ${theme.spacing(0.35)}`: 
            `100% ${theme.spacing(0.35)}` :
        undefined,

    padding: `${theme.spacing(0)} !important`,
    margin: theme.spacing(0),
    transition: 'background-size 0.15s, background-position 0s 0.15s',

    '& >span': {
        fontSize: '1rem',

        '& >div': {
            '& >svg': {
                fontSize: '16px !important',
                marginRight: theme.spacing(1),
            },
            '& >div': {
                display: 'inline-flex'
            }
        }
    },

    '&:hover': {
        backgroundPosition: 'bottom right',
    }
}));

const NavigationMenu = styled(Box)(({ theme}) => ({
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: theme.spacing(0, 1)
}));

const MobileNavigationLink = styled(ListItemButton, { shouldForwardProp: prop => prop !== 'active' })(({ theme, active}: {theme: Theme, active: boolean}) => ({
    padding: theme.spacing(1, 4),
    color: active ?
        theme.palette.mode === 'dark' ?
            undefined :
            theme.palette.primary.dark :
        theme.palette.mode === 'dark' ?
            undefined :
            theme.palette.primary.main,

    '& div': {
        color: theme.palette.mode === 'dark' ?
            undefined :
            theme.palette.primary.main,
    },

    
    background: active ? theme.palette.mode === 'dark' ? darken(theme.palette.common.white, 0.75) : lighten(theme.palette.common.black, 0.92) : undefined, 
}));

const MobileNavigationLinkSecondary = styled(ListItemButton, { shouldForwardProp: prop => prop !== 'active' })(({ theme, active}: {theme: Theme, active: boolean}) => ({
    padding: theme.spacing(1, 4),
    color: active ?
        theme.palette.mode === 'dark' ?
            undefined :
            theme.palette.secondary.dark :
        theme.palette.mode === 'dark' ?
            undefined :
            theme.palette.secondary.main,

    '& div': {
        color: theme.palette.mode === 'dark' ?
            undefined :
            theme.palette.secondary.main,
    },

    
    background: active ? theme.palette.mode === 'dark' ? darken(theme.palette.common.white, 0.75) : lighten(theme.palette.common.black, 0.92) : undefined, 
}));

const Header: FunctionComponent<IHeaderProps> = ({
    menuItems,
    organisationLogoLight,
    organisationLogoDark,
    organisationLogoMobileLight,
    organisationLogoMobileDark,
    organisationName,
    trialName,
    protocolId,
    user,
    overrideTheme
}) => {
    const isMobile = useIsMobile();

    //const [userAnchorEl, setUserAnchorEl] = React.useState<HTMLButtonElement | null>(null);



    const location = useLocation();
    const chosenTheme = useContext(ChosenThemeContext);
    const setChosenTheme = useContext(ChosenThemeActionContext);

    //@ts-ignore
    const changeTheme = useCallback(() => {
        setChosenTheme(chosenTheme === 'dark' ? 'light' : 'dark');
        console.log('Changing theme to: ', chosenTheme);
    }, [setChosenTheme, chosenTheme]);

    const [open, setOpen] = React.useState(false);

    const onOpen = React.useCallback(() => {
        setOpen(true);
    }, [setOpen])

    const onClose = React.useCallback(() => {
        setOpen(false);
    }, [setOpen]);



    
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    


    const activeLinkIndex = useMemo(() => {
        let i: number | undefined = undefined;
        let length: number = 0;

        if (!menuItems || menuItems.length === 0) {
            return i;
        }

        for (let ci = 0; ci < menuItems.length; ci++) {
            const link = menuItems[ci];
            const linkUrl = link.url.includes('?') ? 
                link.url.substring(0, link.url.indexOf('?')) :
                link.url;

            if (location.pathname.startsWith(linkUrl) && linkUrl.length > length) {
                length = linkUrl.length;
                i = ci;
            }
        }

        return i;
    }, [menuItems, location])

    let dividerInserted = false;

    const organisationLogo = chosenTheme === 'dark' ? organisationLogoDark : organisationLogoLight;
    const organisationLogoMobile = chosenTheme === 'dark' ? organisationLogoMobileDark : organisationLogoMobileLight;

    const LogoComponent = user ? LogoBox : PublicLogoBox;
    const theme = useTheme();

    const loginUrl = useMemo(() => `/auth/openiddict?redirect=${encodeURIComponent(location.pathname + location.search)}&theme=${theme.palette.mode}`, [location, theme.palette.mode]);

    return (
        <>
            <StyledAppBar
                position="fixed"
            >
                <StyledToolbar>
                    <LogoContainer>
                        {
                            !!isMobile && !!user && <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="menu"
                                onClick={onOpen}
                            >
                                <FontAwesomeIcon fixedWidth icon={faBars} />
                            </IconButton>
                        }

                        <StyledAnchor>
                            {
                                organisationLogo ?
                                <Button onClick={() => { window.location.href = `https://www.breastcancertrials.org.au/` }} style={{ padding: 0, minWidth: 'unset', height: '64px' }} >
                                    <LogoComponent>
                                        <img
                                            src={organisationLogo}
                                            alt={organisationName}
                                        />
                                    </LogoComponent>
                                </Button> :
                                <Title
                                    variant="h2"
                                    variantMapping={{
                                        h2: 'span'
                                    }}
                                    >
                                        {organisationName}
                                    </Title>
                            }
                        </StyledAnchor>
                        <StyledLink to="/">
                            <TrialNameArea>
                                <TrialName>
                                    {trialName}
                                </TrialName>
                                <TrialProtocolId>
                                    {protocolId}
                                </TrialProtocolId>
                            </TrialNameArea>
                        </StyledLink>
                    </LogoContainer>

                    {
                        !!menuItems && !isMobile && (
                            
                            <MenuContainer>
                                
                                
                                {
                                    menuItems.map((menuItem, i) => {
                                        
                                        if (menuItem.external || menuItem.newTab) {
                                            if (!dividerInserted) {
                                                dividerInserted = true;
                                                return (
                                                    <React.Fragment key={i}>
                                                        <NavigationLink
                                                            //@ts-ignore
                                                            component="a"
                                                            href={menuItem.url}
                                                            color={chosenTheme === 'dark' ? 'inherit' : "secondary" }
                                                            target={menuItem.newTab ? '_blank' : undefined}
                                                            size="small"
                                                        >
                                                            <NavigationMenu>
                                                                <FontAwesomeIcon icon={menuItem.icon} fixedWidth />
                                                                {menuItem.label}
                                                            </NavigationMenu>
                                                        </NavigationLink>
                                                    </React.Fragment>
                                                );
                                            }
                                            return (
                                                <NavigationLink
                                                    key={i}
                                                    //@ts-ignore
                                                    component="a"
                                                    href={menuItem.url}
                                                    color={chosenTheme === 'dark' ? 'inherit' : "secondary" }
                                                    target={menuItem.newTab ? '_blank' : undefined}
                                                    size="small"
                                                >
                                                    <NavigationMenu>
                                                        <FontAwesomeIcon icon={menuItem.icon} fixedWidth />
                                                        {menuItem.label}
                                                    </NavigationMenu>
                                                </NavigationLink>
                                            );

                                        }


                                        return (
                                            <NavigationLink
                                                key={i}
                                                //@ts-ignore
                                                component={Link}
                                                to={menuItem.url}
                                                color="inherit"
                                                size="small"
                                                active={i === activeLinkIndex}
                                            >
                                                <NavigationMenu>
                                                    <FontAwesomeIcon icon={menuItem.icon} fixedWidth />
                                                    {menuItem.label}
                                                </NavigationMenu>
                                            </NavigationLink>
                                        );
                                        
                                    })
                                }
                    <MenuDivider />


                            </MenuContainer>
                        )
                    }
                    <TrialUserContainer>
                        
                        {
                            !overrideTheme && (
                                <>
                                    <Tooltip 
                                        title={chosenTheme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                                    >
                                        <IconButton
                                            aria-label="theme"
                                            color="inherit"
                                            onClick={changeTheme}
                                        >
                                            <FontAwesomeIcon icon={chosenTheme === 'dark' ? faMoon : faSun } fixedWidth />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )
                        }
                        <ToggleFeedbackButton />
                        {
                            (
                                <>
                                {!user?.userId && (
                                    <IconButton
                                        id="btn-auth"
                                        aria-label="account of current user"
                                        aria-haspopup="true"
                                        color="inherit"
                                        component="a"
                                        href={loginUrl}
                                    >

                                        <Typography component="strong" variant='h6' sx={{ fontWeight: 700, lineHeight: '1.6rem' }}>
                                            LOGIN
                                        </Typography>
                                    </IconButton>
                                )}
                            {user?.userId && (
                                <>
                                    <IconButton
                                        id="btn-auth"
                                        aria-label="account of current user"
                                        aria-haspopup="true"
                                            onClick={handleMenu}
                                            color="inherit">
                                            <FontAwesomeIcon icon={faUserCircle} />
                                        </IconButton>
                                        <Menu
                                            id="menu-auth"
                                            anchorEl={anchorEl}
                                            keepMounted
                                            slotProps={{
                                                list: {
                                                    disablePadding: true
                                                }
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'right',
                                            }}
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'right',
                                            }}
                                            open={anchorEl?.id === 'btn-auth'}
                                            onClose={handleClose}
                                        >
                                            {user?.userId ? (
                                                <>
                                                    <MenuItem>{user.displayName ?? user.userName}</MenuItem>
                                                    <Divider 
                                                        style={{ margin: '0 !important' }}
                                                    />
                                                    {(user.roles?.includes('Admin') || user.roles?.includes('TissueBankAdmin')) && (
                                                        <MenuItem component="a" href="/TrialSettings">
                                                            Trial Settings
                                                        </MenuItem>
                                                    )}
                                                    <MenuItem component="a" href="/auth/logout">
                                                        Logout
                                                    </MenuItem>
                                                </>
                                            ) : (
                                                <MenuItem component="a" href="/auth/openiddict">
                                                    Login
                                                </MenuItem>
                                            )}
                                        </Menu>
                                    </>
                                )}
                                </>
                            )
                        }
                        {
                            //!!applyDarkMode && <Tooltip title={darkMode ? 'Dark Mode' : 'Light Mode'}>
                            //    <IconButton
                            //        aria-label="settings"
                            //        aria-controls="menu-settings"
                            //        aria-haspopup="true"
                            //        onClick={toggleMode}
                            //        className={classes.icon}
                            //        color="inherit"
                            //        size="small">
                            //        <FontAwesomeIcon icon={modeIcon} />
                            //    </IconButton>
                            //</Tooltip>
                        }
                    </TrialUserContainer>
                </StyledToolbar>
            </StyledAppBar>
            <SwipeableDrawer
                open={open}
                onClose={onClose}
                onOpen={onOpen}
            >

                {
                    !!organisationLogoMobile && <Box>
                        <LogoMobileImage
                            src={organisationLogoMobile}
                            alt={organisationName}
                        />
                    </Box>
                }

                <Divider />
                <List
                    disablePadding
                >
                    {
                        !!menuItems && menuItems.map((menuItem, i) => {

                            if (menuItem.external || menuItem.newTab) {
                                return (
                                    <React.Fragment
                                        key={i}
                                    >
                                        <MobileNavigationLinkSecondary
                                             //@ts-ignore
                                            component="a"
                                            href={menuItem.url}
                                            color="secondary"
                                            target={menuItem.newTab ? "_blank" : undefined}
                                        >
                                            <ListItemIcon style={{ minWidth: '3rem' }}>
                                                <FontAwesomeIcon icon={menuItem.icon} style={{ fontSize: '1.5rem' }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={menuItem.label}
                                            />
                                        </MobileNavigationLinkSecondary>
                                        <Divider />
                                    </React.Fragment>
                                );
                            }

                            return (
                                <React.Fragment
                                    key={i}
                                >
                                    <MobileNavigationLink
                                        //@ts-ignore
                                        component={NavLink}
                                        to={menuItem.url}
                                        color="inherit"
                                        active={i === activeLinkIndex}
                                    >
                                        <ListItemIcon style={{ minWidth: '3rem' }}>
                                            <FontAwesomeIcon icon={menuItem.icon} style={{ fontSize: '1.5rem' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={menuItem.label}
                                        />
                                    </MobileNavigationLink>
                                    <Divider />
                                </React.Fragment>
                            );
                        })
                    }
                </List>
            </SwipeableDrawer>
        </>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default Header;
